import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Veuillez saisir votre adresse email');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/request-password-reset`, { email });
      setSuccess(true);
    } catch (error) {
      console.error('Password reset request error:', error);
      toast.error('Erreur lors de la demande de réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
        <Card className="w-full max-w-md shadow-2xl border-green-100">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Email envoyé !</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Si cette adresse email est associée à un compte, vous recevrez un lien de réinitialisation dans quelques minutes.
            </p>
            <p className="text-sm text-amber-600">
              Vérifiez également votre dossier spam.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center w-full bg-slate-800 text-white dark:bg-slate-700 px-4 py-3 rounded-lg font-medium"
            >
              Retour à la connexion
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
      <Card className="w-full max-w-md shadow-2xl border-gray-200 dark:border-gray-800 dark:border-gray-800">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
            <Mail className="w-9 h-9 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">Firma</CardTitle>
          <CardDescription className="text-base">
            Mot de passe oublié
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              Saisissez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
            {/* Email field */}
            <div className="space-y-2">
              <Label htmlFor="email">Adresse email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="vous@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="border-gray-200 dark:border-gray-800 focus:border-slate-500"
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={loading || !email}
              data-testid="reset-request-button"
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-medium py-3 text-base disabled:opacity-50"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Link
              to="/login"
              className="inline-flex items-center text-slate-700 dark:text-slate-300 hover:text-slate-800 dark:text-slate-200 font-medium text-sm"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour à la connexion
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;